import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  createAdminUserSchema,
  type CreateUserFormValues,
} from '../../schemas/userSchemas';
import { roleLabels } from '../UserTable/UserTable.columns';
import { ResponsiveCombobox, type ComboboxItem } from '@/components/ui/responsive-combobox';
import { useOrganizations } from '@/features/organizations/hooks/useOrganizationManagement';

interface CreateUserFormProps {
  onSubmit: (values: CreateUserFormValues) => Promise<void>;
  formId?: string;
  onResetComplete?: () => void;
}

export function CreateUserForm({
  onSubmit,
  formId = 'create-form',
  onResetComplete,
}: CreateUserFormProps) {
  const form = useForm<CreateUserFormValues>({
    resolver: zodResolver(createAdminUserSchema),
    defaultValues: {
      name: '',
      email: '',
      phone_number: '',
      role: undefined,
      organization_id: undefined,
      is_active: true,
    },
  });

  // Fetch organizations dynamically
  const { data: orgData, isLoading: isLoadingOrgs } = useOrganizations();
  
  const organizations: ComboboxItem[] = (orgData?.organizations || []).map(org => ({
    value: org.id.toString(),
    label: org.name,
    venueCount: org.venue_count,
  }));

  const handleSubmit = async (values: CreateUserFormValues) => {
    await onSubmit(values);
    form.reset();
    onResetComplete?.();
  };

  return (
    <Form {...form}>
      <form
        id={formId}
        className="space-y-4"
        onSubmit={form.handleSubmit(handleSubmit)}
        data-testid="user-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="John Doe"
                  data-testid="user-form-name"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder="john@example.com"
                  data-testid="user-form-email"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  data-testid="user-form-phone"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger data-testid="user-form-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(roleLabels).map(([value, label]) => (
                    <SelectItem
                      key={value}
                      value={value}
                      data-testid={`user-form-role-option-${value}`}
                    >
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organization (optional)</FormLabel>
              <FormControl>
                <ResponsiveCombobox
                  items={organizations}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder={isLoadingOrgs ? "Loading organizations..." : "Select organization"}
                  searchPlaceholder="Search organizations..."
                  disabled={isLoadingOrgs}
                  data-testid="user-form-org-select"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
